/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, April 11th 2022, 4:46:15 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Checkbox from "@mui/material/Checkbox";
import { FormControl, FormControlLabel } from "@mui/material";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import InfoIcon from "@mui/icons-material/Info";
import Skeleton from "@mui/material/Skeleton";
import { ContextTagSelectorButtonWrapper } from "common/components/context-tags";
import { CopyFromMediaLibrary } from "media-library/components/CopyFromMediaLibrary";
import {
  OSAPIForm,
  TextFormInput,
  DropDownFormInput,
  SwitchFormInput,
  IconText,
  OSActionButton,
  useToggleState,
  StyledOSDialog,
} from "ui-widgets";
import { useDocumentTemplateAPIForm } from "service-builder/services";
import { PrimaryKeyValue } from "lib/types";
import { DocumentTemplate, DOCUMENT_TYPE_OPTIONS, LAYOUT_OPTIONS } from "lib/schemas";
import { DesignLayoutMany2OneInlineInput, LayoutToggler } from "service-builder/components/layouts";
import { useServiceBuilderContext } from "service-builder/providers";
import { TranslatableTextFormInput } from "translation/components";
import { DocumentNumberConfig } from "./DocumentNumberConfig";

interface DocumentTemplateFormProps {
  documentTemplateId: PrimaryKeyValue<DocumentTemplate>;
}

const layoutTypeData: React.ComponentProps<typeof LayoutToggler>["layoutTypeData"] = [
  {
    layoutType: "document",
    layoutTypeTitle: "Document Layout",
    layoutTypeColor: "success",
  },
  {
    layoutType: "qr_code",
    layoutTypeTitle: "QR Scan Layout",
    layoutTypeColor: "default",
  },
];

export const DocumentTemplateForm = ({ documentTemplateId }: DocumentTemplateFormProps) => {
  const {
    currentProcessDefinition,
    useDocumentTemplateAPIFormReturn,
    documentHasValidityPeriod,
    handleChangeDocumentHasValidity,
    editorContentToAppend,
    onCopyContextTag,
    onCopyImage,
    layoutCanvasTitle,
    isQRScanLayout,
    layoutType,
    toggleLayoutType,
    numberingSequenceId,
    documentNumberingType,
    documentTemplateTitle,
    enableDocumentNumbering,
  } = useDocumentTemplateFormLogic(documentTemplateId);

  const { open: detailsOpen, handleOpen: openDetailsFn, handleClose: closeDetailsFn } = useToggleState();

  return (
    <>
      <OSAPIForm
        useAPIFormReturn={useDocumentTemplateAPIFormReturn}
        loaderType="custom"
        customProgressLoader={<DocumentTemplateFormSkeleton />}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ paddingLeft: 2, paddingRight: 2, paddingTop: 2, paddingBottom: 2 }}>
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid item xs={5}>
                    <IconText
                      icon={<DesignServicesIcon color="primary" />}
                      textComponent={<Typography variant="h5">{layoutCanvasTitle}</Typography>}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={1}>
                      <div style={{ marginLeft: "auto" }}>
                        <OSActionButton variant="contained" onClick={openDetailsFn}>
                          Edit Document Info
                        </OSActionButton>{" "}
                        <ContextTagSelectorButtonWrapper
                          onCopyContextTag={onCopyContextTag}
                          copiedMessage="Appended to Template!!"
                          contextTagsAPIFilters={{ process_definition: currentProcessDefinition.id }}
                        >
                          {({ openContextTagsFn }) => (
                            <OSActionButton onClick={openContextTagsFn} variant="contained">
                              Use Context Tags
                            </OSActionButton>
                          )}
                        </ContextTagSelectorButtonWrapper>{" "}
                        <DocumentNumberConfig
                          documentTemplateTitle={documentTemplateTitle}
                          documentNumberingType={documentNumberingType}
                          enableDocumentNumbering={enableDocumentNumbering}
                          numberingSequenceId={numberingSequenceId}
                        />{" "}
                        <CopyFromMediaLibrary
                          label="Copy Images From Media Library"
                          successLabel="Image appended to template!!"
                          buttonProps={{ variant: "contained" }}
                          onChooseAsset={(metaData) => onCopyImage(metaData.media_item_url, metaData.media_item_name)}
                        />
                      </div>
                    </Stack>
                  </Grid>
                  <Grid item xs={1}>
                    <LayoutToggler
                      selectedLayoutType={layoutType}
                      toggleLayoutType={toggleLayoutType}
                      layoutTypeData={layoutTypeData}
                    />
                  </Grid>
                </Grid>
                {isQRScanLayout ? (
                  <>
                    {/** TODO: Find out why for some reason the conditional render has no effect unless we include state in the render. */}
                    {isQRScanLayout}
                    <DesignLayoutMany2OneInlineInput<DocumentTemplate>
                      layout_type="qr_code"
                      schema_field_key="qr_data_layout"
                      appendContents={editorContentToAppend?.qr_code}
                    />
                  </>
                ) : (
                  <DesignLayoutMany2OneInlineInput<DocumentTemplate>
                    layout_type="general"
                    schema_field_key="layout"
                    appendContents={editorContentToAppend?.document}
                  />
                )}
              </Stack>
            </Paper>
          </Grid>

          <StyledOSDialog
            open={detailsOpen}
            onClose={closeDetailsFn}
            popUpIcon={<InfoIcon color="primary" />}
            popUpTitle={<Typography variant="h5">Document Details</Typography>}
          >
            {/** <form> ensures the form submit event is fired and propagated to parent form */}
            <form>
              <Stack spacing={2}>
                <TranslatableTextFormInput<DocumentTemplate>
                  schema_field_key="title"
                  label="Document Title"
                  fullWidth
                />
                <DropDownFormInput<DocumentTemplate>
                  schema_field_key="document_type"
                  options={DOCUMENT_TYPE_OPTIONS}
                  label="Document Type"
                />
                <FormControl>
                  <FormControlLabel
                    label="Will documents generated by this template have a validity period?"
                    control={
                      <Checkbox checked={documentHasValidityPeriod} onChange={handleChangeDocumentHasValidity} />
                    }
                  />
                </FormControl>

                {documentHasValidityPeriod ? (
                  <TextFormInput<DocumentTemplate> schema_field_key="valid_for" label="Validity period (In days)" />
                ) : null}

                <SwitchFormInput<DocumentTemplate>
                  schema_field_key="one_valid_template"
                  label="Restrict to only one valid document per process at any given time?"
                />

                <OSActionButton variant="contained" type="submit" onClick={closeDetailsFn}>
                  Save Details
                </OSActionButton>
              </Stack>
            </form>
          </StyledOSDialog>
        </Grid>
      </OSAPIForm>
    </>
  );
};

const DocumentTemplateFormSkeleton = () => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={8}>
        <Skeleton animation="wave" variant="rectangular" height={750} />
      </Grid>
      <Grid item xs={2}>
        <Skeleton animation="wave" variant="rectangular" width={300} height={400} />
      </Grid>
    </Grid>
  );
};

type LayoutTagContent = { [key in keyof typeof LAYOUT_OPTIONS]?: string };

const useDocumentTemplateFormLogic = (documentTemplateId: PrimaryKeyValue<DocumentTemplate>) => {
  const { useDocumentTemplateAPIFormReturn, documentHasValidityPeriod, handleChangeDocumentHasValidity } =
    useDocumentTemplateAPIForm(documentTemplateId);

  const [editorContentToAppend, setEditorContentToAppend] = React.useState<LayoutTagContent>(null);

  const [layoutType, setLayoutType] = React.useState<keyof typeof LAYOUT_OPTIONS>("document");

  const onCopyContextTag = ({ tag, tagName }) => setEditorContentToAppend({ [layoutType]: `${tagName}: ${tag}` });

  const onCopyImage = (imageURL: string, imageName?: string) =>
    setEditorContentToAppend({ [layoutType]: `<img src='${imageURL}' alt='${imageName || "image"}' />` });

  const toggleLayoutType = (layoutType: keyof typeof LAYOUT_OPTIONS) => {
    setLayoutType(layoutType);
    setEditorContentToAppend(null); // Reset Context Tag content when layout is toggled
  };

  const [watchedDocumentTitle, watchedSequence, enableDocumentNumbering, documentNumberingType] =
    useDocumentTemplateAPIFormReturn.useFormReturn.watch([
      "title",
      "sequence",
      "enable_document_numbering",
      "document_numbering_type",
    ]);

  const isQRScanLayout = layoutType === "qr_code";

  const layoutCanvasTitle = `${isQRScanLayout ? "QR Scan" : "Document"} Layout Design for ${watchedDocumentTitle}`;

  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  return {
    currentProcessDefinition,
    useDocumentTemplateAPIFormReturn,
    documentHasValidityPeriod,
    handleChangeDocumentHasValidity,
    editorContentToAppend,
    onCopyContextTag,
    layoutCanvasTitle,
    isQRScanLayout,
    layoutType,
    toggleLayoutType,
    numberingSequenceId: watchedSequence,
    documentTemplateTitle: watchedDocumentTitle,
    enableDocumentNumbering,
    documentNumberingType,
    onCopyImage,
  };
};
